/**
 * Green Flag — raw design tokens
 *
 * These are the lowest-level primitive values — brand-agnostic developers
 * SHOULD NOT reference them directly in components. Use the semantic tokens
 * in `SemanticTokens.ts` instead (e.g. `bg.brand` not `gf.green.500`).
 *
 * Source of truth: Figma "green flag.fig" (audited).
 */

import { defineTokens } from "@chakra-ui/react";

export const tokens = defineTokens({
  colors: {
    gf: {
      // ---- Brand green scale ----
      // 500 is the core brand green (#39B87D). The rest are composed
      // tints/shades of that hue, harmonized in OKLCH for smooth ramps.
      green: {
        50:  { value: "#E7F6EF" },
        100: { value: "#CDEDDE" }, // mint (active pill, attending chip)
        200: { value: "#A3D8C0" }, // soft green (inactive nav glyph)
        300: { value: "#7AC4A2" },
        400: { value: "#55BE8F" },
        500: { value: "#39B87D" }, // ★ brand green
        600: { value: "#2E9A69" },
        700: { value: "#236F4D" },
        800: { value: "#2B4338" }, // forest ink (text on mint)
        900: { value: "#1A2921" },
      },

      // ---- Friends mode accent ----
      blue: {
        50:  { value: "#E4F2FB" },
        100: { value: "#BEDEF4" },
        300: { value: "#59A8DE" },
        500: { value: "#0B80CD" }, // ★ friends blue
        700: { value: "#075D94" },
        900: { value: "#04344F" },
      },

      // ---- Rejection / notification red ----
      red: {
        100: { value: "#FDDBDB" },
        500: { value: "#EF3F3F" }, // ★ reject X, notification dot
        700: { value: "#B22424" },
        // Compat-only: mapped by `brand.red.*` aliases (lib/chakraTheme.ts).
        legacyPrimary: { value: "#EC5F5F" },
        legacyDark:    { value: "#5B1414" },
      },

      // ---- Compat-only palettes (lib/chakraTheme.ts aliases) ----
      orange: {
        primary: { value: "#EF863F" },
        dark:    { value: "#3F2616" },
      },
      neutral: {
        dark:   { value: "#000000" },
        medium: { value: "#E0E0E0" },
        light:  { value: "#F5F1EF" },
      },
      gray: {
        primary: { value: "#A5A5A5" },
        medium:  { value: "#878787" },
        dark:    { value: "#757270" },
      },

      // ---- Neutrals ----
      ink: {
        900: { value: "#21242A" }, // primary text / icons on white
        600: { value: "#636363" }, // muted icons / helper text
      },
      line: {
        strong: { value: "#D9D9D9" }, // hairline stroke, placeholder
        soft:   { value: "#EDEDED" }, // softer stroke (search bar)
      },
      surface: {
        white:    { value: "#FFFFFF" },
        subtle:   { value: "#F5F5F5" }, // inactive pill bg
        muted:    { value: "#EDEDED" }, // page-level gray behind frames
      },
    },
  },

  // ---- Translucent color tokens (can't live in `colors` scale cleanly) ----
  // Used as raw `{colors.gf.greenAlpha.25}` tokens for the Sparks chip.
  // (Chakra supports raw colors here; we keep as regular `colors` tokens.)

  fonts: {
    body:    { value: "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
    heading: { value: "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
    display: { value: "'League Gothic', 'Poppins', sans-serif" },
  },

  fontWeights: {
    light:    { value: "300" },
    normal:   { value: "400" },
    medium:   { value: "500" },
    semibold: { value: "600" },
    bold:     { value: "700" },
  },

  fontSizes: {
    "2xs": { value: "8px"  }, // micro bold (chip)
    xs:    { value: "9px"  }, // notification badge number
    sm:    { value: "10px" }, // eyebrows, nav labels
    md:    { value: "12px" }, // button/chip labels, small body
    lg:    { value: "14px" }, // card body
    xl:    { value: "16px" }, // logo mobile, body large
    "2xl": { value: "18px" }, // card title (desktop)
    "3xl": { value: "24px" }, // screen headline mobile, logo desktop
    "4xl": { value: "36px" }, // hero headline desktop
  },

  lineHeights: {
    tight: { value: "1.255" }, // Figma default
    base:  { value: "1.6"   }, // logo / League Gothic
    loose: { value: "1.93"  }, // light body on desktop
  },

  letterSpacings: {
    tight:  { value: "-0.01em" },
    normal: { value: "0"       },
    wide:   { value: "0.02em"  }, // logo
    wider:  { value: "0.04em"  }, // eyebrows, button labels
    widest: { value: "0.06em"  }, // section eyebrows
  },

  radii: {
    none: { value: "0"     },
    xs:   { value: "5px"   }, // images, chips
    sm:   { value: "8px"   }, // cards
    md:   { value: "12px"  },
    lg:   { value: "16px"  },
    pill: { value: "100px" }, // buttons, tabs
    full: { value: "9999px" }, // avatars, dots
  },

  spacing: {
    // 4pt scale
    1:  { value: "4px"  },
    2:  { value: "8px"  },
    3:  { value: "12px" },
    4:  { value: "16px" },
    5:  { value: "20px" },
    6:  { value: "24px" },
    7:  { value: "27px" }, // mobile gutter (Figma-specific)
    8:  { value: "32px" },
    10: { value: "40px" },
    12: { value: "48px" },
    16: { value: "64px" },
  },

  sizes: {
    phone: { value: "402px" }, // iPhone 16/17 Pro canvas (Figma)
    web:   { value: "1512px" }, // MacBook Pro 14" canvas (Figma)
    webContent: { value: "680px" }, // centered column on desktop
    // Admin and host workspaces (AdminShell)
    adminTopBar:  { value: "56px"   }, // top bar height, every width
    adminSidebar: { value: "240px"  }, // expanded sidebar, from lg
    adminRail:    { value: "72px"   }, // icon rail, md to lg
    adminDrawer:  { value: "280px"  }, // phone nav drawer
    adminContent: { value: "1200px" }, // widest admin content column
    hostContent:  { value: "720px"  }, // the check-in column
  },

  shadows: {
    card:     { value: "0 0 12.1px 2px rgba(0, 0, 0, 0.05)"  },  // event/match cards
    image:    { value: "0 2px 2px 0 rgba(0, 0, 0, 0.10)"     },  // image tile inside carousel
    lift:     { value: "0 0 12.1px 2px rgba(0, 0, 0, 0.25)"  },  // floating action buttons
    header:   { value: "0 4px 8px 0 rgba(0, 0, 0, 0.10)"     },  // desktop top nav
    bottomNav:{ value: "-4px 1px 4.9px 1px rgba(0, 0, 0, 0.25)" },// bottom nav bar
    focusRing:{ value: "0 0 0 2px rgba(57, 184, 125, 0.45)"  },  // brand focus ring
    brandGlow:{ value: "0 4px 12px rgba(57, 184, 125, 0.3)"  },  // primary CTA glow
    adminDrawer:{ value: "0 8px 24px rgba(0, 0, 0, 0.16)"    },  // admin phone nav drawer
  },

  durations: {
    fast:   { value: "80ms"  }, // press transform
    normal: { value: "120ms" }, // color / opacity
  },

  easings: {
    standard: { value: "cubic-bezier(0.2, 0, 0, 1)" },
  },
});
